import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Alert, Button, Input, Segmented, Space, Spin, Tag, message, notification } from 'antd'
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { openApi } from '../../../helpers/request'
import cartsController from './carts.controller'
import { cartsFilterChips, cartsFilterQuery, errorMessage, formatPhone, parseCartsSegment } from './carts.helpers'
import './carts.less'

type Delimiter = ';' | ','

interface ExportRow {
    userId: number
    name: string | null
    phone: string | null
    email: string | null
    registered: boolean
    cartId: number
    sum: number
    positions: number
    activityAt: string | null
    warehouse: string | null
}

interface ExportResult {
    rows: ExportRow[]
    carts: number
    truncated: boolean
}

/** Значение ячейки CSV: в кавычки — если внутри разделитель, кавычка или перенос строки */
const cell = (value: string, delimiter: Delimiter): string => value.includes(delimiter) || /["\r\n]/.test(value)
    ? `"${value.replace(/"/g, '""')}"`
    : value

/**
 * Русский Excel ждёт «;» между колонками и запятую в дробях — с «;» сумму пишем через запятую,
 * иначе он прочитает её как текст или дату. Телефон — с пробелами, чтобы Excel не превратил его в число.
 */
const toCsv = (rows: ExportRow[], delimiter: Delimiter): string => {
    const withWarehouse = rows.some(row => row.warehouse)
    const header = [
        i.t('carts:export.columns.name'),
        i.t('carts:export.columns.phone'),
        i.t('carts:export.columns.email'),
        i.t('carts:export.columns.sum'),
        i.t('carts:export.columns.positions'),
        i.t('carts:export.columns.activity'),
        ...(withWarehouse ? [i.t('carts:export.columns.warehouse')] : []),
        i.t('carts:export.columns.userId'),
    ]

    const lines = rows.map(row => [
        row.name ?? '',
        formatPhone(row.phone),
        row.email ?? '',
        delimiter === ';' ? `${row.sum}`.replace('.', ',') : `${row.sum}`,
        `${row.positions}`,
        row.activityAt ? dayjs(row.activityAt).format('DD.MM.YYYY HH:mm') : '',
        ...(withWarehouse ? [row.warehouse ?? ''] : []),
        `${row.userId}`,
    ])

    return [header, ...lines].map(line => line.map(value => cell(value, delimiter)).join(delimiter)).join('\r\n')
}

export const CartsExport = observer(() => {

    const history = useHistory()
    const location = useLocation()
    const segment = useMemo(
        () => parseCartsSegment(new URLSearchParams(location.search).get('carts')) ?? { filters: {}, labels: {} },
        [location.search]
    )
    const chips = cartsFilterChips(segment)

    const [result, setResult] = useState<ExportResult | null>(null)
    const [loading, setLoading] = useState(true)
    const [delimiter, setDelimiter] = useState<Delimiter>(';')

    useEffect(() => {
        const filter = cartsFilterQuery(segment.filters)
        setLoading(true)

        openApi('get', `/carts/export${filter ? `?${filter}` : ''}`)
            .then(response => setResult(response.data))
            .catch(err => notification.error({ message: i.t('carts:errors.common'), description: errorMessage(err) }))
            .finally(() => setLoading(false))
    }, [segment])

    const rows = result?.rows ?? []
    const text = useMemo(() => toCsv(rows, delimiter), [rows, delimiter])

    const copy = () => navigator.clipboard?.writeText(text)
        .then(() => message.success(i.t('carts:export.copied')))
        .catch(() => message.error(i.t('carts:export.copyFailed')))

    // Файл собирается прямо в браузере и никуда не загружается. BOM — чтобы Excel понял UTF-8 и не исказил кириллицу
    const download = () => {
        const url = URL.createObjectURL(new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' }))
        const link = document.createElement('a')
        link.href = url
        link.download = `carts-users-${dayjs().format('YYYY-MM-DD')}.csv`
        link.click()
        URL.revokeObjectURL(url)
    }

    return (
        <>
            <Titles
                title={i.t('carts:export.title')}
                breadcrumb={[{ title: i.t('carts:title'), href: '/carts' }, { title: i.t('carts:export.title') }]}
                buttons={[{
                    title: i.t('carts:backToList'),
                    type: 'dashed',
                    // Список — с теми же условиями, что у выгрузки
                    click: () => {
                        cartsController.applySegment(segment)
                        history.push('/carts')
                    },
                }]}
            />

            <div className="carts-export">
                <div className="carts-export__conditions">
                    <span className="carts-muted">{i.t('carts:export.conditions')}</span>
                    {chips.length > 0
                        ? chips.map(chip => <Tag key={chip} className="carts-tag">{chip}</Tag>)
                        : <Tag className="carts-tag">{i.t('carts:export.allCarts')}</Tag>}
                </div>

                {loading && <div className="carts-export__loading"><Spin /></div>}

                {!loading && result && (
                    <>
                        <div className="carts-export__summary">
                            <div className="carts-stat">
                                <div className="carts-stat__value">{result.carts.toLocaleString()}</div>
                                <div className="carts-stat__label">{i.t('carts:export.carts')}</div>
                            </div>
                            <div className="carts-stat carts-stat--green">
                                <div className="carts-stat__value">{rows.length.toLocaleString()}</div>
                                <div className="carts-stat__label">{i.t('carts:export.withContacts')}</div>
                            </div>
                        </div>

                        <Alert type="info" showIcon message={i.t('carts:export.guestsHint')} />

                        {result.truncated && (
                            <Alert type="warning" showIcon message={i.t('carts:export.truncated', { n: rows.length.toLocaleString() })} />
                        )}

                        {rows.length > 0 && (
                            <>
                                <div className="carts-export__toolbar">
                                    <Space wrap>
                                        <span className="carts-muted">{i.t('carts:export.delimiter')}</span>
                                        <Segmented
                                            value={delimiter}
                                            onChange={(value: any) => setDelimiter(value)}
                                            options={[
                                                { label: i.t('carts:export.semicolon'), value: ';' },
                                                { label: i.t('carts:export.comma'), value: ',' },
                                            ]}
                                        />
                                    </Space>
                                    <Space wrap>
                                        <Button icon={<CopyOutlined />} onClick={copy}>{i.t('carts:export.copy')}</Button>
                                        <Button type="primary" icon={<DownloadOutlined />} onClick={download}>{i.t('carts:export.download')}</Button>
                                    </Space>
                                </div>

                                <Input.TextArea
                                    readOnly
                                    value={text}
                                    className="carts-export__text"
                                    autoSize={{ minRows: 8, maxRows: 24 }}
                                    onFocus={(event) => event.target.select()}
                                />
                                <div className="carts-muted">{i.t('carts:export.hint')}</div>
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    )
})
